/**
 * The bridge from the PDP to the quiz — teaser and popup.
 *
 * A/B pdp_quiz_unlock_2608, arm B only, and only while the discount is still
 * to be earned. It replaces Klaviyo's popup rather than joining it: that form
 * offers "up to 30% off" for an email, which on this page is a second,
 * different discount competing with the one being tested — and the one on top
 * is the one nobody is measuring.
 *
 * It asks for nothing. Klaviyo's popup trades an email for a discount; this
 * one is a doorway, and the quiz behind it collects the address anyway, with
 * consent and thirteen answers attached. So there is no form here, no
 * validation and no consent to handle, and nobody is asked twice.
 *
 * ── The number ──────────────────────────────────────────────────────────────
 *
 * It names the plan the shopper is actually looking at. The PDP always has one
 * selected — Monthly by default — so the popup can say "22% off your monthly
 * plan" rather than "up to 36%", which would be true and still feel like a
 * bait to the 1-month buyer who finds their real figure four screens into a
 * quiz. The ceiling still appears, but as an upgrade line rather than the
 * headline: the first number is theirs and is never a disappointment, and the
 * second is an invitation to trade up in the one place trading up is a live
 * decision.
 *
 * ── When it fires ───────────────────────────────────────────────────────────
 *
 * Twenty seconds, or an exit gesture, whichever comes first — someone leaving
 * at eight seconds will never see the twenty-second version, and that is
 * exactly when a better price is worth raising.
 *
 * Closing the popup reveals the teaser: the offer stays recoverable rather than
 * being taken away for good. Closing the teaser ends it for the visit. Both are
 * held in sessionStorage, so they last as long as the tab does and a genuine
 * return visit starts clean.
 */
(function () {
  var HTML = document.documentElement;
  var SESSION_KEY = '_fleur_quiz_bridge_dismissed';
  var TEASER_KEY = '_fleur_quiz_teaser_dismissed';

  var TAG = document.querySelector('script[src*="quiz-bridge"]');
  function setting(name, fallback) {
    var v = TAG && TAG.getAttribute(name);
    return v === null || v === undefined || v === '' ? fallback : v;
  }

  /* Arm A is the control and must be the page as it stands, popup included. */
  if (HTML.getAttribute('data-ab3') !== 'b') return;

  /* Read when it is asked, not remembered from page load: the quiz is a panel
     over the product page now, so this flips mid-visit with no navigation. */
  function earned() {
    return HTML.getAttribute('data-quiz-unlock') === 'yes';
  }

  function dismissed() {
    try { return window.sessionStorage.getItem(SESSION_KEY) === '1'; } catch (e) { return false; }
  }
  function remember() {
    try { window.sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}
  }

  /* Read from the selected plan's offer panel, which Liquid has already
     resolved per variant — no second source of truth for the ladders. */
  function currentOffer() {
    var box = document.querySelector('.custom-variant-box');
    var card = box && (box.querySelector('.variant-card.active') || box.querySelector('.variant-card'));
    var panel = card && card.querySelector('.plan_offer[data-plan-unlock]');
    if (panel) {
      var title = card.querySelector('.title');
      return {
        unlock: panel.getAttribute('data-plan-unlock'),
        href: panel.getAttribute('data-plan-href') || '',
        plan: title ? title.textContent.trim().toLowerCase() : 'subscription'
      };
    }
    /* Everywhere else. Naming a plan is the product page's privilege — nowhere
       else has one selected — so the floor is quoted instead: the figure every
       completer is guaranteed on the cheapest plan, which is the one number
       that cannot read as a bait once they finish the quiz. */
    return {
      unlock: setting('data-floor-unlock', ''),
      href: setting('data-quiz-url', '/pages/hair-quiz-discount'),
      plan: 'subscription'
    };
  }

  /* The best figure on offer anywhere on the page, for the upgrade line. Read
     rather than configured, so it follows the ladders like everything else. */
  function ceiling() {
    var best = 0;
    var nodes = document.querySelectorAll('.plan_offer[data-plan-unlock]');
    for (var i = 0; i < nodes.length; i++) {
      var v = Number(nodes[i].getAttribute('data-plan-unlock')) || 0;
      if (v > best) best = v;
    }
    return best;
  }

  function styles() {
    if (document.getElementById('fleur-bridge-css')) return;
    var css = document.createElement('style');
    css.id = 'fleur-bridge-css';
    css.textContent = [
      /* Klaviyo's surfaces, off. Same selectors the quiz page proved out: the
         id-based ones miss the modal, which Klaviyo wraps in a
         kl-private-reset-css-XXXXX class generated per render, so that one is
         matched on its prefix. */
      '.klaviyo-form-X7UkCf,[class*="klaviyo-form-X7UkCf"],[class*="kl-teaser-X7UkCf"],',
      '.kl-scroll-locking-modal,[class*="kl-scroll-locking-modal"],[class*="kl-private-reset-css"]',
      '{display:none !important;visibility:hidden !important;pointer-events:none !important}',
      /* Klaviyo locks the body by adding a class of its own. With the modal
         hidden that lock has nothing to hold and simply strands the page —
         which is what "the page gets stuck" was: after the quiz this file used
         to stand down entirely, Klaviyo's popup came back, and its lock stayed
         on with nothing visible to explain it.

         Scoped to Klaviyo's class rather than set on `body` outright. A blanket
         override also undoes the cart drawer's own lock, which is legitimate
         and should keep working. */
      /* overflow-y only. The theme's normal state is `hidden auto` — horizontal
         suppressed on purpose — and restoring `visible` on both axes would hand
         back a horizontal scrollbar while fixing the vertical one. */
      'body.klaviyo-prevent-body-scrolling{overflow-y:auto !important}',

      '.fbridge{position:fixed;z-index:9000;font-family:"Neue Haas Unica W1G","Neue Haas Unica",sans-serif}',

      /* Teaser: a rectangle in the brand red with sand type, the same pairing
         the quiz itself runs on, so the thing it opens is recognisable before
         it opens. A rectangle rather than a pill because a pill reads as a
         chip — a label — and this is a control.

         Bottom-left, not centre. It is the persistent half of this pair: the
         popup is transient and can afford the middle of the screen, but
         something that stays belongs in a corner where it is not standing over
         the page for the rest of the session. */
      '.fbridge-teaser{left:50%;bottom:16px;transform:translateX(-50%);display:flex;align-items:center;',
      'border-radius:10px;background:#812221;color:#e8d5ac;',
      'box-shadow:0 8px 24px rgba(0,0,0,.22)}',
      /* Centred on a phone, where the middle of the bottom edge is where the
         thumb is. On a desktop that same position sits under the middle of a
         wide viewport, over the content, with nothing to anchor it — so it
         moves to the corner, which is where a persistent control belongs. */
      '@media (min-width:990px){.fbridge-teaser{left:20px;transform:none}}',
      '.fbridge-teaser[hidden]{display:none}',
      /* The label and the dismiss are separate controls inside the rectangle —
         one rectangle, two jobs. Nesting a button inside a button would not be
         valid, and the dismiss has to be reachable on its own. */
      '.fbridge-teaser-go{display:flex;align-items:center;gap:9px;padding:13px 8px 13px 17px;',
      'border:0;background:none;color:inherit;font:inherit;font-size:14.5px;font-weight:600;',
      'letter-spacing:-.1px;cursor:pointer;border-radius:10px 0 0 10px}',
      '.fbridge-teaser-x{border:0;background:none;color:inherit;cursor:pointer;',
      'padding:13px 15px 13px 10px;font-size:16px;line-height:1;opacity:.65;border-radius:0 10px 10px 0}',
      '.fbridge-teaser-x:hover{opacity:1}',
      '.fbridge-teaser-go:hover{opacity:.85}',

      /* Panel: a sheet on a phone, a card above it on a desktop. */
      /* A rectangle floating at the bottom of the screen, not a modal.
         The full-bleed sheet behind a scrim was a stop-everything gesture for
         something the shopper had not asked for; this sits in front of the page
         without taking it over, so ignoring it costs nothing. No backdrop, so
         the page stays scrollable and clickable underneath. */
      '.fbridge-scrim{left:50%;bottom:16px;transform:translateX(-50%);width:calc(100% - 24px);max-width:380px}',
      '.fbridge-scrim[hidden]{display:none}',
      '.fbridge-panel{position:relative;background:#fff;border:1px solid #ece7e2;border-radius:12px;',
      'padding:18px 18px 16px;text-align:center;box-shadow:0 10px 34px rgba(0,0,0,.17)}',
      '.fbridge-head{margin:0 0 6px;font-size:18px;font-weight:600;line-height:1.2;letter-spacing:-.3px;color:#1f1a17}',
      '.fbridge-body{margin:0 0 14px;font-size:13px;line-height:1.4;color:#4a4340}',
      '.fbridge-body b{color:#812221;font-weight:600}',
      '.fbridge-go{display:block;padding:12px;border-radius:8px;background:#812221;color:#fff;',
      'font-size:13.5px;font-weight:600;text-decoration:none}',
      '.fbridge-no{display:block;width:100%;margin:9px 0 0;padding:2px;border:0;background:none;',
      'color:#797575;font-size:12px;text-decoration:underline;cursor:pointer}',
      '.fbridge-x{position:absolute;top:6px;right:8px;border:0;background:none;font-size:18px;',
      'line-height:1;color:#9a9694;cursor:pointer;padding:4px 8px;z-index:2}',

      /* The image column and the veil exist in the markup at every width but
         are inert below the desktop breakpoint — the phone layout is the one
         that was signed off and nothing here may disturb it. */
      '.fbridge-media,.fbridge-veil{display:none}',

      /* ── Desktop ──────────────────────────────────────────────────────────
         On a phone the rectangle at the bottom of the screen is close to the
         thumb and close to the price. On a desktop that same rectangle is a
         small object stranded in the corner of a wide viewport, far from where
         anyone is looking, and reads as an ad rather than an offer. So above
         990px it becomes what it should be there: a card in the middle of the
         screen, wide enough to carry the product image beside the offer.

         The veil comes back with it. It was dropped when this was a bottom
         sheet, and rightly — a sheet that dims the whole page to say one
         sentence is a stop-everything gesture. A dialog in the centre of the
         screen is different: with nothing behind it the page keeps scrolling
         under a thing that will not move, which reads as broken. It stays
         light, and clicking it does not dismiss — the two explicit controls
         are still the only way out. */
      '@media (min-width:990px){',
        '.fbridge-veil{display:block;position:fixed;inset:0;z-index:8999;',
        'background:rgba(31,26,23,.34)}',
        '.fbridge-veil[hidden]{display:none}',

        '.fbridge-scrim{top:50%;left:50%;bottom:auto;transform:translate(-50%,-50%);',
        'width:calc(100% - 48px);max-width:660px}',

        /* Two columns, image first. 260px is wide enough for the bottle to be
           recognisable at a glance without taking the sentence off the card. */
        '.fbridge-panel{display:grid;grid-template-columns:260px 1fr;padding:0;',
        'text-align:left;overflow:hidden;border-radius:14px;',
        'box-shadow:0 24px 60px rgba(0,0,0,.28)}',
        /* No image to show — a variant with no media, or a DOM this cannot
           read — collapses to one column rather than leaving a grey slab. */
        '.fbridge-panel--noimg{grid-template-columns:1fr}',
        '.fbridge-panel--noimg .fbridge-media{display:none}',

        '.fbridge-media{display:block;background:#3a1a10 center/cover no-repeat;min-height:300px}',

        '.fbridge-content{padding:36px 38px 32px}',
        /* Large enough to be the first thing read. The phone size was set by
           the width of the rectangle; here the constraint is gone. */
        '.fbridge-head{margin:0 0 10px;font-size:30px;line-height:1.12;letter-spacing:-.6px}',
        '.fbridge-body{margin:0 0 22px;font-size:15px;line-height:1.5}',
        /* A button sized to its words rather than stretched across the column:
           a full-width bar under a half-width card looks like a form control. */
        '.fbridge-go{display:inline-block;padding:14px 28px;font-size:14.5px;border-radius:9px}',
        '.fbridge-no{width:auto;margin:14px 0 0;text-align:left;font-size:12.5px}',
        '.fbridge-x{top:10px;right:12px;font-size:20px}',
      '}'
    ].join('');
    document.head.appendChild(css);
  }

  var scrim, teaser, veil;

  function teaserGone() {
    try { return window.sessionStorage.getItem(TEASER_KEY) === '1'; } catch (e) { return false; }
  }

  function close(permanent) {
    if (scrim) scrim.hidden = true;
    if (veil) veil.hidden = true;
    if (permanent) remember();
    if (teaser && !teaserGone()) teaser.hidden = false;
  }

  /* The quiz's own background, handed over on the script tag by
     sections/longform-product.liquid.

     Not the product image, which was the first thing tried and was wrong: the
     popup floats over a page already showing that exact shot at ten times the
     size, so the panel read as a duplicate rather than as design. Nor a second
     gallery image — every one of those is an infographic with type baked into
     it, and a 260px column crops them straight through the words.

     This one is abstract and portrait, so it crops safely at any height, and it
     is what the shopper sees the moment they arrive: the panel previews the
     destination instead of repeating the page. */
  function paintMedia() {
    if (!scrim) return false;
    var media = scrim.querySelector('[data-fb-media]');
    var src = setting('data-panel-image', '');
    if (!media || !src) return false;
    media.style.backgroundImage = 'url("' + src + '")';
    return true;
  }

  function open() {
    var offer = currentOffer();
    if (!offer || !offer.unlock) return;
    if (!scrim) return;
    var top = ceiling();

    /* The offer stated, in the headline, with the plan's own number.
       The question mark belongs on the teaser, which interrupts and so has to
       ask; by the time this panel is open the shopper has either been shown it
       or opened it themselves, and a second question would be asking twice. */
    scrim.querySelector('[data-fb-head]').textContent = 'Get ' + offer.unlock + '% off';
    scrim.querySelector('[data-fb-body]').innerHTML =
      'A few questions about your hair and we will apply it to your ' +
      offer.plan + '.' +
      (top > Number(offer.unlock) ? ' <b>Up to ' + top + '% on longer plans.</b>' : '');
    scrim.querySelector('[data-fb-go]').setAttribute('href', offer.href);

    var panel = scrim.querySelector('[data-fb-panel]');
    if (panel) panel.classList.toggle('fbridge-panel--noimg', !paintMedia());

    scrim.hidden = false;
    if (veil) veil.hidden = false;
    if (teaser) teaser.hidden = true;
  }

  function build() {
    styles();

    teaser = document.createElement('div');
    teaser.className = 'fbridge fbridge-teaser';
    teaser.hidden = true;
    teaser.innerHTML =
      '<button type="button" class="fbridge-teaser-go" data-fb-teaser-go>' +
      '<span data-fb-teaser-text>Care for a discount?</span><span aria-hidden="true">→</span></button>' +
      '<button type="button" class="fbridge-teaser-x" data-fb-teaser-x aria-label="Dismiss">×</button>';
    teaser.addEventListener('click', function (e) {
      if (e.target.closest('[data-fb-teaser-x]')) {
        /* Dismissing the teaser is final for the session. The popup's own
           dismissal leaves this behind so the offer stays recoverable; closing
           this one is the shopper saying they are done being asked. */
        teaser.hidden = true;
        try { window.sessionStorage.setItem(TEASER_KEY, '1'); } catch (err) {}
        return;
      }
      open();
    });

    scrim = document.createElement('div');
    scrim.className = 'fbridge fbridge-scrim';
    scrim.hidden = true;
    scrim.innerHTML =
      '<div class="fbridge-panel" data-fb-panel role="dialog" aria-modal="true" aria-labelledby="fb-h">' +
      '<button type="button" class="fbridge-x" data-fb-x aria-label="Close">×</button>' +
      /* Decorative: the sentence beside it already says what this is, so a
         description here would only be read out twice. */
      '<div class="fbridge-media" data-fb-media role="presentation"></div>' +
      '<div class="fbridge-content">' +
      '<p class="fbridge-head" id="fb-h" data-fb-head></p>' +
      '<p class="fbridge-body" data-fb-body></p>' +
      '<a class="fbridge-go" data-fb-go href="#">Take the 2-minute quiz</a>' +
      '<button type="button" class="fbridge-no" data-fb-no>Not now</button>' +
      '</div></div>';

    /* Only the two explicit dismissals. There is no backdrop to click any more,
       and a stray click on the card itself must not close the thing the shopper
       may be reading. */
    scrim.addEventListener('click', function (e) {
      if (e.target.hasAttribute('data-fb-x') || e.target.hasAttribute('data-fb-no')) close(true);
    });

    veil = document.createElement('div');
    veil.className = 'fbridge-veil';
    veil.hidden = true;

    document.body.appendChild(veil);
    document.body.appendChild(teaser);
    document.body.appendChild(scrim);
  }

  /* The teaser asks — it is the half that interrupts, and an interruption that
     asks is easier to ignore than one that announces. It names the figure too,
     so what was dismissed stays recognisable as an offer. */
  function paintTeaser() {
    if (!teaser) return;
    var offer = currentOffer();
    var el = teaser.querySelector('[data-fb-teaser-text]');
    if (offer && offer.unlock && el) el.textContent = 'Care for ' + offer.unlock + '% off?';
  }

  /* The announcement bar names the same offer, so it names the same number.
     Liquid ships the floor — 22%, the cheapest plan's figure — and this raises
     it to the selected plan's, which is the safe direction: if this never runs,
     the bar understates what is on offer rather than promising more than the
     quiz will deliver.

     querySelectorAll rather than one lookup: the bar is a swiper, and a looping
     swiper clones its slides, so the message can exist more than once. */
  function paintBar() {
    var offer = currentOffer();
    if (!offer || !offer.unlock) return;
    var nodes = document.querySelectorAll('.ab3-bar-quiz [data-ab3-pct]');
    for (var i = 0; i < nodes.length; i++) nodes[i].textContent = offer.unlock;
  }

  function repaint() {
    paintTeaser();
    paintBar();
  }

  function arm() {
    repaint();
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.variant-card')) {
        setTimeout(repaint, 60);
      }
    });

    /* Already asked this session: no popup, just the teaser — unless that has
       been closed too, in which case nothing. */
    if (dismissed()) { if (teaser && !teaserGone()) teaser.hidden = false; return; }
    if (teaserGone()) return;

    var fired = false;
    function fire() {
      if (fired || dismissed() || teaserGone()) return;
      fired = true;
      open();
    }

    /* Twenty seconds. Long enough that it arrives to someone who has settled
       into the page rather than someone still deciding whether to stay. */
    window.setTimeout(fire, 20000);

    /* ...or on the way out, whichever comes first. Someone leaving at eight
       seconds is not going to see the twenty-second version, and a better
       price is worth raising exactly then. */
    document.addEventListener('mouseout', function (e) {
      if (!e.relatedTarget && e.clientY <= 0) fire();
    });
  }

  /* Klaviyo locks the page by putting a class on <body>, and it does that from
     its own script whether or not its modal is visible. Hiding the modal
     therefore produced the worst version of it: the scroll stopped and there
     was nothing on screen to explain why. That is what "the page gets stuck
     when the popup pops up" was — the popup was Klaviyo's, and it was invisible.

     The class is removed rather than overridden. A stylesheet fight is not
     winnable here: Klaviyo's styles load after ours, so at equal specificity
     theirs win, and matching their exact selector only escalates it. Taking the
     class off ends it, and it re-runs whenever they put it back.

     Only the class they own is touched. The theme has its own body locks —
     body--hidden, sub-gifts-modal-open, lab-modal-open — and those are
     legitimate and left alone. */
  function unstick() {
    var LOCK = 'klaviyo-prevent-body-scrolling';
    function clear() {
      try {
        if (document.body && document.body.classList.contains(LOCK)) {
          document.body.classList.remove(LOCK);
        }
      } catch (e) {}
    }
    clear();
    if (!window.MutationObserver || !document.body) return;
    new MutationObserver(clear).observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /* The announcement bar's earned message, on pages that have no plan cards to
     read it from. Liquid ships the floor including the SMS bonus; once that has
     lapsed the figure is the ladder alone, and left alone the bar would go on
     claiming the larger number. On the product page quiz-earned.js does this
     from the card actually in the cart, so this stands aside there. */
  function fixEarnedBar() {
    if (document.querySelector('.custom-variant-box')) return;
    var hasBonus = /(^|;)\s*_fleur_quiz_bonus\s*=/.test(document.cookie);
    if (hasBonus) return;
    var pct = setting('data-floor-ladder', '');
    if (!pct) return;
    var nodes = document.querySelectorAll('.ab3-bar-earned [data-ab3-earned-pct]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].textContent !== pct) nodes[i].textContent = pct;
    }
  }

  function start() {
    /* The suppression runs for all of arm B. It is what keeps Klaviyo's "up to
       30% off" from sitting on top of the discount being measured, and its
       scroll lock off a page that has no popup to justify it. */
    styles();
    unstick();
    /* The offer itself is only for someone who has not earned it yet. */
    if (earned()) { fixEarnedBar(); return; }

    /* Not on the quiz itself. Now that this loads on every page it would
       otherwise interrupt someone halfway through the questionnaire to ask
       whether they would care for a discount, and offer them a link to the page
       they are already on. The suppression above still applies here — Klaviyo's
       popup over a form is the thing it was written for. */
    if (document.querySelector('[data-hair-quiz]')) return;
    build();
    arm();
  }

  /* And again the moment the quiz says so, so the announcement bar stops
     inviting her to take a quiz she has just finished. */
  document.addEventListener('quiz:unlocked', function () {
    try { fixEarnedBar(); } catch (e) {}
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
